// frontend/src/hooks/useAuth.js
// Hook personalizado para gestionar la lógica de autenticación.

import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { syncEntraSession } from '../api/auth';
import { getEntraSession, loginWithEntra, logoutFromEntra } from '../lib/entraAuth';

/**
 * @function useAuth
 * @description Hook para manejar el inicio de sesión y cierre de sesión.
 * Proporciona métodos para autenticar usuarios y el estado de autenticación.
 * @returns {{
 * isAuthenticated: boolean,
 * isInitializing: boolean,
 * login: function(): Promise<void>,
 * logout: function(): Promise<void>,
 * initializeAuth: function(): Promise<void>
 * }}
 */
export function useAuth() {
  const {
    isAuthenticated,
    isInitializing,
    setAuthData,
    clearAuthData,
    setIsInitializing,
    setAccessProvisioningRequired,
  } = useAuthStore();
  const { setIsLoading, showTemporaryMessage } = useUIStore();

  const isProvisioningError = (error) => error?.message?.toLowerCase().includes('provisionado');

  /**
   * @function handleLogin
   * @description Intenta autenticar al usuario con Microsoft Entra ID.
   * @returns {Promise<void>}
   */
  const handleLogin = useCallback(async () => {
    setIsLoading(true); // Activa el spinner de carga
    setAccessProvisioningRequired(false);
    try {
      const session = await loginWithEntra();
      const response = await syncEntraSession(session.token || session.accessToken);
      // Guardar tanto el token como los datos del usuario
      setAuthData(response.token || session.token || session.accessToken, response.user, session.account);
      showTemporaryMessage('¡Inicio de sesión exitoso con Microsoft Entra ID!', 'success');
    } catch (error) {
      clearAuthData();
      if (isProvisioningError(error)) {
        setAccessProvisioningRequired(true);
        return;
      }
      showTemporaryMessage(error.message || 'Error al iniciar sesión con Microsoft Entra ID.', 'error');
    } finally {
      setIsLoading(false); // Desactiva el spinner de carga
    }
  }, [clearAuthData, setAccessProvisioningRequired, setAuthData, setIsLoading, showTemporaryMessage]);

  const initializeAuth = useCallback(async () => {
    setIsInitializing(true);
    setAccessProvisioningRequired(false);
    try {
      const session = await getEntraSession();
      if (!session?.token && !session?.accessToken) {
        clearAuthData();
        return;
      }

      const response = await syncEntraSession(session.token || session.accessToken);
      setAuthData(response.token || session.token || session.accessToken, response.user, session.account);
    } catch (error) {
      clearAuthData();
      if (isProvisioningError(error)) {
        setAccessProvisioningRequired(true);
      }
    } finally {
      setIsInitializing(false);
    }
  }, [clearAuthData, setAccessProvisioningRequired, setAuthData, setIsInitializing]);

  const handleLogout = useCallback(async () => {
    clearAuthData();
    setAccessProvisioningRequired(false);
    try {
      await logoutFromEntra();
    } catch (error) {
      console.error('No se pudo cerrar sesión en Microsoft Entra ID:', error);
    }
  }, [clearAuthData, setAccessProvisioningRequired]);

  return {
    isAuthenticated,
    isInitializing,
    login: handleLogin,
    logout: handleLogout,
    initializeAuth,
  };
}
